// ---------------------------------------------------------------------------
// RelationshipDetailPanel — shows selected node's usage summary and
// connected path chain in the data relationship graph.
// ---------------------------------------------------------------------------

import { useRelationshipHighlight } from '../hooks/use-relationship-highlight'
import { parseOntologyId } from './data-relationship-graph'
import { Database, Zap, Variable, Component, ArrowRight } from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  selectedNodeId: string | null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RelationshipDetailPanel({ selectedNodeId }: Props) {
  const { connectedSet, usageDetails } = useRelationshipHighlight(selectedNodeId)

  if (!selectedNodeId || !connectedSet) {
    return (
      <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
        Click a node to see relationships
      </div>
    )
  }

  const parsed = parseOntologyId(selectedNodeId)
  const connectedNodes = [...connectedSet].filter((id) => id !== selectedNodeId)

  // Group connected by type
  const connectedDs = connectedNodes.filter((id) => id.startsWith('ds:'))
  const connectedFlows = connectedNodes.filter((id) => id.startsWith('flow:'))
  const connectedState = connectedNodes.filter((id) => id.startsWith('state:'))
  const connectedUi = connectedNodes.filter((id) => id.startsWith('ui:'))

  return (
    <div className="border-t border-border bg-muted/30 px-3 py-2 text-xs space-y-1.5 max-h-[200px] overflow-auto">
      {/* Selected node header */}
      <div className="flex items-center gap-2 font-medium text-foreground">
        {parsed.type === 'datasource' && <Database size={12} className="text-amber-600" />}
        {parsed.type === 'flow' && <Zap size={12} className="text-primary" />}
        {parsed.type === 'state' && <Variable size={12} className="text-blue-600" />}
        {parsed.type === 'uiBinding' && <Component size={12} className="text-purple-600" />}
        <span>
          {parsed.type}: {parsed.key ?? parsed.flowName ?? parsed.blockName}
        </span>
      </div>

      {/* Usage details for state / datasource nodes */}
      {usageDetails && (
        <div className="flex items-center gap-3 text-muted-foreground">
          {usageDetails.readCount > 0 && <span>{usageDetails.readCount} read</span>}
          {usageDetails.writeCount > 0 && <span>{usageDetails.writeCount} write</span>}
          {usageDetails.bindingCount > 0 && <span>{usageDetails.bindingCount} binding</span>}
          {usageDetails.flowCount > 0 && (
            <span>
              {usageDetails.flowCount} flow{usageDetails.flowCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Connected path chain */}
      <div className="flex flex-wrap items-center gap-1 text-muted-foreground">
        {connectedDs.length > 0 && (
          <span className="text-amber-600">
            {connectedDs.length} datasource{connectedDs.length !== 1 ? 's' : ''}
          </span>
        )}
        {connectedDs.length > 0 && connectedFlows.length > 0 && <ArrowRight size={10} />}
        {connectedFlows.length > 0 && (
          <span className="text-primary">
            {connectedFlows.length} flow{connectedFlows.length !== 1 ? 's' : ''}
          </span>
        )}
        {connectedFlows.length > 0 && connectedState.length > 0 && <ArrowRight size={10} />}
        {connectedState.length > 0 && <span className="text-blue-600">{connectedState.length} state</span>}
        {connectedState.length > 0 && connectedUi.length > 0 && <ArrowRight size={10} />}
        {connectedUi.length > 0 && <span className="text-purple-600">{connectedUi.length} UI</span>}
      </div>
    </div>
  )
}
